import { Link } from "react-router-dom";
import { Crown, Hourglass, Sparkles, Gift, Scroll, ArrowRight } from "lucide-react";

/**
 * Quick Links bar — 4 clickable tiles that take visitors directly to
 * the dedicated /waitlist, /register, /sponsor, and /rules pages.
 * Replaces the old in-place forms on the homepage so the home stays short.
 */
const TILES = [
  {
    to: "/waitlist",
    testid: "quick-link-waitlist",
    color: "#d4af37",
    Icon: Hourglass,
    label: "Waiting List",
    blurb: "Cohort is filling up — claim your spot.",
  },
  {
    to: "/register",
    testid: "quick-link-register",
    color: "#d62288",
    Icon: Crown,
    label: "Sign-up Sheet",
    blurb: "Register your queen (or queenz) today.",
  },
  {
    to: "/sponsor",
    testid: "quick-link-sponsor",
    color: "#7d289b",
    Icon: Gift,
    label: "Sponsor a Queen",
    blurb: "Crown her future — give once or monthly.",
  },
  {
    to: "/rules",
    testid: "quick-link-rules",
    color: "#d4af37",
    Icon: Scroll,
    label: "Rules & Regulations",
    blurb: "The guidelines that keep every queen safe.",
  },
];

export function QuickLinks() {
  return (
    <section
      id="quick-links"
      data-testid="quick-links-section"
      className="relative py-16 sm:py-20 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #ffd9e8 0%, #d68aaf 30%, #6f2347 60%, #1d0712 88%, #000 100%)",
      }}
    >
      <Sparkles className="absolute top-10 left-[6%] h-8 w-8 text-[#f6e27a]/60 floaty" aria-hidden />
      <Crown className="absolute bottom-10 right-[6%] h-10 w-10 text-[#f6e27a]/40 floaty-slow" aria-hidden />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="flex items-center gap-3 mb-4">
            <img
              src="/logo-sm.png"
              alt="Queenz Like Me"
              className="h-14 w-14 rounded-full object-cover ring-2 ring-[#f6e27a] shadow-xl"
            />
            <span className="h-px w-20 bg-gradient-to-r from-[#f6e27a] to-transparent" />
          </div>
          <p className="font-script text-3xl text-[#f6e27a]">Quick Access</p>
          <h2 className="mt-1 font-display text-3xl sm:text-5xl font-bold text-white">
            Everything you need, one tap away.
          </h2>
          <p className="mt-3 text-white/85 max-w-xl">
            Click any tile to open its own page — no scrolling required.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TILES.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className="text-left rounded-3xl p-6 bg-white/95 hover:bg-white shadow-2xl hover:-translate-y-1 transition-all border-2 block"
              style={{ borderColor: t.color }}
            >
              <div
                className="h-12 w-12 rounded-2xl flex items-center justify-center"
                style={{ background: `${t.color}22`, border: `1.5px solid ${t.color}66` }}
              >
                <t.Icon className="h-6 w-6" style={{ color: t.color }} />
              </div>
              <h3 className="mt-4 font-display text-xl font-bold text-[#1a0f14]">{t.label}</h3>
              <p className="mt-1.5 text-sm text-[#5c4a54] leading-relaxed">{t.blurb}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: t.color }}>
                Open <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
